import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';
import { MatTable, MatTableModule } from '@angular/material/table';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { EmployeeDto } from '../../dtos/employee.dto';
import { ErrorService } from '../../../../core/services/error.service';
import { EmployeeService } from '../../services/employee.service';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { EmployeeFormFactory } from '../../mappers/employee-form.factory';
import { EmployeeModel } from '../../models/employee.model';
import { EmployeeMapper } from '../../mappers/employee.mapper';
import { LocationSummaryDto } from '../../../location/dtos/location-summary.dto';
import { CategoryDto } from '../../../category/dtos/category.dto';
import { EmployeeDataDto } from '../../dtos/employee-data.dto';
import { UpdateEmployeeDto } from '../../dtos/update-employee.dto';
import { BookingConstraintDto } from '../../../../shared/dtos/booking-constraint.dto';
import {
  RestrictBookingTimesDialog
} from '../../../../shared/dialogs/restrict-booking-times/restrict-booking-times.dialog';
import { FormFactory } from '../../../../shared/mappers/form.factory';
import { LocalizedTextPipe } from '../../../../shared/pipes/localized-text.pipe';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-manage-employee',
  imports: [MatCardModule, MatDialogModule, MatButtonModule, MatFormField, MatInputModule, MatCheckboxModule, ReactiveFormsModule, MatTableModule, MatIconModule, MatMenuModule, CommonModule, LocalizedTextPipe, TranslocoPipe],
  templateUrl: './manage-employee.dialog.html',
  styleUrl: './manage-employee.dialog.scss'
})
export class ManageEmployeeDialog implements OnInit {
  @ViewChild('table') table!: MatTable<any>;
  employeeForm!: FormGroup;

  imagePreview?: string;
  imageError?: string;
  selectedFile?: File;

  hadImage!: boolean;
  imageHasChanged: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      employeeDto?: EmployeeDto,
      locationSummaryDtos: LocationSummaryDto[],
      categoryDtos: CategoryDto[]
    },
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ManageEmployeeDialog>,
    private errorService: ErrorService,
    private employeeService: EmployeeService,
    private translocoService: TranslocoService,
    private dialog: MatDialog
  ) {
  }

  get bookingConstraints(): FormArray {
    return this.employeeForm.get('bookingConstraints') as FormArray;
  }

  get locations(): (LocationSummaryDto | undefined)[] {
    return [undefined, ...this.data.locationSummaryDtos];
  }

  get categoryIds(): FormArray {
    return this.employeeForm.get('categoryIds') as FormArray;
  }

  get selectedCategoryDtos(): CategoryDto[] {
    const categoryIds: number[] = this.employeeForm.get('categoryIds')?.value;
    if (!categoryIds) {
      return [];
    }

    const categoryMap = new Map(
      this.data.categoryDtos.map(category => [category.categoryId, category])
    );

    return categoryIds
      .map(id => categoryMap.get(id))
      .filter((category): category is CategoryDto => !!category);
  }

  ngOnInit(): void {
    if (!this.data.employeeDto) {
      const emptyEmployeeModel: EmployeeModel = {
        bookingConstraints: [],
        categoryIds: [],
        canBook: false
      };
      this.employeeForm = EmployeeFormFactory.createEmployeeForm(this.fb, emptyEmployeeModel);
      return;
    }

    this.imagePreview = this.data.employeeDto.imageUrl;
    this.hadImage = this.imagePreview != undefined;
    this.employeeForm = EmployeeFormFactory.createEmployeeForm(this.fb, EmployeeMapper.toModel(this.data.employeeDto));
  }

  close(): void {
    this.dialogRef.close();
  }

  selectFile(event: any): void  {
    const input : HTMLInputElement = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];

    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        //image dimension check
        const maxDimension = 1500;
        if (img.width > maxDimension || img.height > maxDimension) {
          this.imageError = this.translocoService.translate('error.image.dimension', {width: img.width, height: img.height, maxDimension: maxDimension});
          this.imageHasChanged = false;
          return;
        }

        //image size check
        const maxSize = 1024 * 1024;
        if (file.size > maxSize) {
          this.imageError = this.translocoService.translate('error.image.size', {size: (file.size / 1024 / 1024).toFixed(1), maxSize: 1});
          this.imageHasChanged = false;
          return;
        }

        const formData = new FormData();
        formData.append('imageFile', file, file.name);

        this.imagePreview = reader.result as string;
        this.imageError = undefined;
      };

      img.src = reader.result as string;
    };

    reader.readAsDataURL(file);
    this.selectedFile = file;
    this.imageHasChanged = true;
  }

  deleteImage(): void {
    this.imagePreview = undefined;
    this.selectedFile = undefined;
    this.imageError = undefined;
    this.imageHasChanged = true;
  }

  addCategory(categoryId: number): void {
    if (this.categoryIds.value.includes(categoryId)) {
      return;
    }

    this.categoryIds.push(new FormControl(categoryId));
    this.table.renderRows();
  }

  deleteCategory(index: number): void {
    this.categoryIds.removeAt(index);
    this.table.renderRows();
  }

  getCategoriesByLocation(locationId?: number): CategoryDto[] {
    return this.data.categoryDtos.filter(category => category.locationId == locationId);
  }

  showLocationName(location?: LocationSummaryDto): string {
    if (!location) {
      return this.translocoService.translate("label.location.null");
    }

    return location.name;
  }

  showLocationNameById(locationId?: number): string {
    if (!locationId) {
      return this.showLocationName(undefined);
    }

    let location = this.data.locationSummaryDtos.find(location => location.locationId == locationId);
    return this.showLocationName(location);
  }

  createEmployee(): void {
    if (this.employeeForm.invalid) {
      this.employeeForm.markAllAsTouched();
      return;
    }

    let employeeModel: EmployeeModel = this.employeeForm.value;
    let employeeDataDto: EmployeeDataDto = EmployeeMapper.toDto(employeeModel);
    this.employeeService.addEmployee(employeeDataDto).subscribe({
      next: (employeeDto: EmployeeDto) => {
        this.manageImage(employeeDto);
      },
      error: (error) => {
        this.errorService.handleError(error, this.translocoService.translate("error.title.add_employee"));
      }
    });
  }

  updateEmployee(): void {
    if (this.employeeForm.invalid) {
      this.employeeForm.markAllAsTouched();
      return;
    }

    let employeeModel: EmployeeModel = this.employeeForm.value;
    let updateEmployeeDto: UpdateEmployeeDto = EmployeeMapper.toDto(employeeModel);
    this.employeeService.updateEmployee(updateEmployeeDto).subscribe({
      next: (employeeDto: EmployeeDto) => {
        this.manageImage(employeeDto);
      },
      error: (error) => {
        this.errorService.handleError(error, this.translocoService.translate("error.title.edit_employee"));
      }
    });
  }

  manageImage(employeeDto: EmployeeDto): void {
    if (!this.imagePreview) {
      if (this.hadImage) {
        this.employeeService.deleteEmployeeImage(employeeDto.employeeId).subscribe({
          next: () => {
            this.dialogRef.close(true);
          },
          error: (error) => {
            this.errorService.handleError(error, this.translocoService.translate("error.title.delete_image"));
          }
        });
        return;
      }

      this.dialogRef.close(true);
      return;
    }

    if (!this.imageHasChanged || !this.selectedFile) {
      this.dialogRef.close(true);
      return;
    }

    let formData: FormData = new FormData();
    formData.append('imageFile', this.selectedFile, this.selectedFile.name);

    this.employeeService.uploadEmployeeImage(this.employeeForm.value.employeeId, formData).subscribe({
      next: () => {
        this.dialogRef.close(true);
      },
      error: (error) => {
        this.errorService.handleError(error, this.translocoService.translate("error.title.add_image"));
      }
    });
  }

  restrictBookingTimes(): void {
    let bookingConstraintDtos: BookingConstraintDto[] = this.employeeForm.get('bookingConstraints')?.value;

    this.dialog
      .open(RestrictBookingTimesDialog, { autoFocus: false, data: bookingConstraintDtos })
      .afterClosed()
      .subscribe((bookingConstraints?: BookingConstraintDto[]) => {
        if (bookingConstraints) {
          this.employeeForm.setControl('bookingConstraints', FormFactory.createBookingConstraintsForm(this.fb, bookingConstraints ?? []));
        }
      });
  }
}
